import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import { updateCharger } from '../../services/chargerService';
import { getWallboxAdminDetails, updateWallboxDesiredMaxAmperes } from '../../services/chargerService';

const EditChargerModal = ({ isOpen, onClose, charger, onChargerUpdated }) => {
  const [formData, setFormData] = useState({
    device_id: '',
    product_id: '',
    category: '',
    uuid: '',
  });
  const [wallboxDetails, setWallboxDetails] = useState(null);
  const [desiredMaxAmperes, setDesiredMaxAmperes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  useEffect(() => {
    if (charger) {
      setFormData({
        device_id: charger.device_id || '',
        product_id: charger.product_id || '',
        category: charger.category || '',
        uuid: charger.uuid || '',
      });

      if (charger.controller_type === 'wallbox') {
        const initialDesired = charger.desired_max_amperes ?? '';
        setDesiredMaxAmperes(String(initialDesired));
      } else {
        setDesiredMaxAmperes('');
        setWallboxDetails(null);
      }
    }
  }, [charger]);

  useEffect(() => {
    const fetchWallboxDetails = async () => {
      if (!isOpen || !charger || charger.controller_type !== 'wallbox') return;
      try {
        const details = await getWallboxAdminDetails(charger.charger_id);
        setWallboxDetails(details);
        if (details?.desired_max_amperes !== undefined && details?.desired_max_amperes !== null) {
          setDesiredMaxAmperes(String(details.desired_max_amperes));
        }
      } catch (e) {
        // No bloqueamos la edición si el detalle falla
        setWallboxDetails(null);
      }
    };

    fetchWallboxDetails();
  }, [isOpen, charger]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setFeedback({ type: '', message: '' });
    try {
      if (charger?.controller_type === 'wallbox') {
        const parsed = Number(desiredMaxAmperes);
        if (!Number.isInteger(parsed) || parsed < 6 || parsed > 40) {
          throw new Error('El límite deseado debe ser un entero entre 6 y 40 A.');
        }
        await updateWallboxDesiredMaxAmperes(charger.charger_id, parsed, false);
      }

      await updateCharger(charger.charger_id, formData);
      setFeedback({ type: 'success', message: 'Cargador actualizado correctamente.' });
      setTimeout(() => {
        onChargerUpdated();
        onClose();
      }, 1500);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al actualizar el cargador.';
      setFeedback({ type: 'error', message: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Editar Cargador</DialogTitle>
      <DialogContent>
        {charger?.controller_type === 'wallbox' && (
          <>
            <TextField
              margin="dense"
              name="desired_max_amperes"
              label="Límite deseado (A)"
              type="number"
              fullWidth
              variant="standard"
              value={desiredMaxAmperes}
              onChange={(e) => setDesiredMaxAmperes(e.target.value)}
              inputProps={{ min: 6, max: 40, step: 1 }}
              helperText="Rango permitido: 6–40 A"
            />
            <TextField
              margin="dense"
              name="actual_max_amperes"
              label="Actual en Wallbox (A)"
              type="text"
              fullWidth
              variant="standard"
              value={
                wallboxDetails?.actual_max_amperes === null || wallboxDetails?.actual_max_amperes === undefined
                  ? 'No disponible'
                  : String(wallboxDetails.actual_max_amperes)
              }
              disabled
            />
          </>
        )}

        <TextField
          autoFocus
          margin="dense"
          name="device_id"
          label="Device ID"
          type="text"
          fullWidth
          variant="standard"
          value={formData.device_id}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="product_id"
          label="Product ID"
          type="text"
          fullWidth
          variant="standard"
          value={formData.product_id}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="category"
          label="Categoría"
          type="text"
          fullWidth
          variant="standard"
          value={formData.category}
          onChange={handleChange}
        />
        <TextField
          margin="dense"
          name="uuid"
          label="UUID"
          type="text"
          fullWidth
          variant="standard"
          value={formData.uuid}
          onChange={handleChange}
        />
        {feedback.message && (
          <Alert severity={feedback.type} sx={{ mt: 2 }}>
            {feedback.message}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? <CircularProgress size={24} /> : 'Actualizar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditChargerModal;
